import React, { useEffect, useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import Modal from "antd/es/modal/Modal";
import moment from "moment";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";

import { getSampleIdEditHistory } from "../../../../../actions/reports";

import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";

import "./SampleIdChangeHistoryModal.css";

export const SAMPLE_ID_HISTORY_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "previous_sample_id", label: "Previous Sample ID" },
  { key: "new_sample_id", label: "New Sample ID", accent: true },
  { key: "last_updated_by", label: "Last updated by", accent: true },
];

export const SAMPLE_TYPE_HISTORY_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "previous_sample_type", label: "Previous Sample Type" },
  { key: "new_sample_type", label: "New Sample Type", accent: true },
  { key: "last_updated_by", label: "Last updated by", accent: true },
];

function formatHistoryDate(dateValue) {
  if (!dateValue) {
    return "-";
  }

  const parsed = moment(dateValue);
  if (!parsed.isValid()) {
    return "-";
  }

  return parsed.format("MM/DD/YYYY, hh:mm a");
}

function getCellValue(entry, columnKey) {
  if (columnKey === "date") {
    return formatHistoryDate(entry.edited_at);
  }
  return entry[columnKey] || "-";
}

function TruncatedCell({ value, accent = false }) {
  let cellClassName = "SampleIdChangeHistoryModal__CellText";
  if (accent) {
    cellClassName += " SampleIdChangeHistoryModal__CellText--accent";
  }

  return (
    <StyledTooltip title={value} placement="top">
      <span className={cellClassName}>
        {value}
      </span>
    </StyledTooltip>
  );
}

export default function SampleIdChangeHistoryModal({
  reportData,
  onClose,
  columns = SAMPLE_ID_HISTORY_COLUMNS,
  fetchHistory = getSampleIdEditHistory,
  receivedDateFormat = "YYYY-MM-DD",
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [historyEntries, setHistoryEntries] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function loadHistory() {
      setIsLoading(true);

      try {
        let receivedDate = "";
        if (reportData.received_date) {
          receivedDate = moment(new Date(reportData.received_date)).format(receivedDateFormat);
        }

        const response = await fetchHistory({
          sample_id: reportData.sample_id,
          sample_type: reportData.sample_type || "Product",
          received_date: receivedDate,
        });

        if (!isMounted) {
          return;
        }

        if (!response.success) {
          toast.error(response.message);
          setHistoryEntries([]);
          return;
        }

        setHistoryEntries(response.history);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        toast.error(error.message || "Failed to load change history");
        setHistoryEntries([]);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [reportData, fetchHistory, receivedDateFormat]);

  return (
    <Modal
      open
      closable={false}
      centered
      footer={null}
      onCancel={onClose}
      wrapClassName="SampleIdChangeHistoryModal"
      className="SampleIdChangeHistoryModal"
      width="853px"
      destroyOnClose
    >
      <div className="SampleIdChangeHistoryModal__Header">
        <span>Change History</span>
        <CloseOutlined
          className="SampleIdChangeHistoryModal__CloseIcon"
          aria-label="Close"
          onClick={onClose}
        />
      </div>

      <div className="SampleIdChangeHistoryModal__Body">
        {isLoading && (
          <div className="SampleIdChangeHistoryModal__Loading">
            <LoadingDots width={28} height={28} fill="#19305A" />
          </div>
        )}

        {!isLoading && historyEntries.length === 0 && (
          <div className="SampleIdChangeHistoryModal__Empty">
            No change history found
          </div>
        )}

        {!isLoading && historyEntries.length > 0 && (
          <div className="SampleIdChangeHistoryModal__TableParent">
            <StyledScrollbar className="SampleIdChangeHistoryModal__Scrollbar">
              <div className="SampleIdChangeHistoryModal__Table">
                <div className="SampleIdChangeHistoryModal__HeaderRow">
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      className="SampleIdChangeHistoryModal__HeaderCell"
                    >
                      {column.label}
                    </div>
                  ))}
                </div>

                {historyEntries.map((entry, index) => (
                  <div
                    key={`${entry.edited_at}-${index}`}
                    className="SampleIdChangeHistoryModal__DataRow"
                  >
                    {columns.map((column) => (
                      <div
                        key={column.key}
                        className="SampleIdChangeHistoryModal__DataCell"
                      >
                        <TruncatedCell
                          value={getCellValue(entry, column.key)}
                          accent={column.accent}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </StyledScrollbar>
          </div>
        )}
      </div>
    </Modal>
  );
}
